/** @jsxImportSource theme-ui */
import { Box } from "theme-ui";
import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { ChartDataset, ChartData, BarController, LineController, Legend } from "chart.js";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip, ChartOptions
} from "chart.js";

import { Chart } from "react-chartjs-2";
import { FilterValue } from "../../../../../types/dashboard.types";
import FilterButton from "../../Components/FilterButton";
import OptionList from "../../Components/OptionList";
import ProjectProgressChartSkeleton from "../../skeleton/ProjectProgressSkeleton";
import CustomMonthSelect from "../../Components/CustomMothSelect";
import { useSelector } from "react-redux";

const forceLineOnTop = {
  id: "forceLineOnTop",
  afterDatasetsDraw(chart: any) {
    chart.data.datasets.forEach((dataset: any, i: number) => {
      if (dataset.type === "line") {
        chart.getDatasetMeta(i).controller.draw();
      }
    });
  },
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  BarController,
  LineController,
  Legend,
  forceLineOnTop
);

const ProjectProgressionChart = ({
  popoverRef,
  graphData,
  value,
  isPopoverOpen,
  handleProjectProgressionFilterClick,
  togglePopover,
  loading,
  months,
  showMonthYearPicker,
  toggleYearMonthHandle,
  colorMode,
  handleAllMonthSelection,
  handleAllYearSelection,

}: {
  popoverRef: any;
  graphData: any;
  value: FilterValue
  isPopoverOpen: string | null;
  handleProjectProgressionFilterClick: (option: string, name: string, flagValue?: string) => void;
  togglePopover: (event?: any) => void;
  loading: boolean;
  months: any;
  showMonthYearPicker: boolean;
  colorMode: string
  toggleYearMonthHandle: any;
  handleAllMonthSelection: any;
  handleAllYearSelection: any,

}) => {

  const chartRef = useRef<ChartJS<"bar" | "line"> | null>(null);
  const [ticks, setTicks] = useState<number[]>([]);
  const [search, setSearch] = useState("")
  const CHART_HEIGHT = 420;
  const { flag } = useSelector((state: any) => state.dashboardData || {});
  /* -------- Normalize Data -------- */
  const processedGraphData = useMemo(() => {
    let data: any[] = [];
    if (Array.isArray(graphData)) {
      data = [...graphData];
    } else if (graphData && typeof graphData === "object" && Array.isArray(graphData.data)) {
      data = [...graphData.data];
    }

    // If only 1 data point, wrap it with 0-start and 0-end points
    if (data.length === 1) {
      data.unshift({
        month: "",
        done: null,
        approved: null,
        cumulativeApproved: 0
      });
      data.push({
        month: "",
        done: null,
        approved: null,
        cumulativeApproved: 0 // Assumed 0 based on symmetric visual request
      });
    }

    return data;
  }, [graphData]);

  const barThickness = value.progressionTime === "Historic" || value.progressionTime === "YTD" || processedGraphData?.length <= 12 ? 57 : 48
  const graphWidth = value.progressionTime === "Historic" || value.progressionTime === "YTD" || processedGraphData?.length <= 12 ? 117 : 100
  const bgDashboard = colorMode === 'light' ? "#ffffff" : "#252b36";
  const graphlabelColor = colorMode === 'light' ? "#453521" : "#f8f9fa";
  const { years } = useSelector((state: any) => state.dashboardData || {});
  function getMaxValue(data: any[]): number {
    return data.reduce((max, item) => {
      return Math.max(
        max,
        item.done ?? 0,
        item.approved ?? 0,
        item.cumulativeApproved ?? 0
      );
    }, 0);
  }
  function getYAxisConfig(maxValue: number) {
    if (maxValue <= 10) {
      return { step: 1, max: 10 };
    }

    if (maxValue <= 100) {
      return { step: 20, max: 100 };
    }

    if (maxValue <= 500) {
      return { step: 100, max: 500 };
    }

    // max >= 500
    const roundedMax = Math.ceil(maxValue / 500) * 500;
    return { step: 500, max: roundedMax };
  }

  function generateYLabels(max: number, step: number): number[] {
    const labels: number[] = [];
    for (let i = 0; i <= max; i += step) {
      labels.push(i);
    }
    return labels;
  }


  const yAxisData = useMemo(() => {
    if (!Array.isArray(processedGraphData) || processedGraphData?.length === 0) {
      return { labels: [0], max: 10, step: 1 };
    }

    const maxValue = getMaxValue(processedGraphData);
    const { step, max } = getYAxisConfig(maxValue);

    return {
      labels: generateYLabels(max, step),
      max,
      step,
    };
  }, [processedGraphData]);


  /** -------- Chart Options -------- */
  const options: ChartOptions<"bar" | "line"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },

    scales: {
      x: {
        type: "category",
        offset: true,

        ticks: {
          autoSkip: false,          // ← CRITICAL
          color: graphlabelColor,
          maxRotation: 0,
          minRotation: 0,
          padding: 8,
          font: {
            size: 10,
          },
        },

        grid: {
          display: true,
          color: "#f4ede6",
          lineWidth: 1,
        },

        border: { display: false },
      },


      y: {
        display: true,
        beginAtZero: true,
        max: yAxisData.max,
        ticks: {
          display: false,
          stepSize: yAxisData.step,
          precision: 0
        },
        grid: {
          display: true,
          color: "#f4ede6",
          lineWidth: 1,
        },
        border: { display: false },
      },
      yLine: {
        beginAtZero: true,
        display: false,
        grid: { drawOnChartArea: false }, // 👈 important
      },
    },
  };

  useEffect(() => {
    if (isPopoverOpen === "" || isPopoverOpen === null) {
      setSearch("")
    }
  }, [isPopoverOpen])

  /** -------- Extract ticks -------- */
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const yscale = chart.scales.y;
    if (!yscale) return;

    setTicks(yscale.ticks.map(t => t.value));
  }, [processedGraphData, yAxisData.max, yAxisData.step]);

  /** -------- Convert ticks to pixel positions -------- */
  const tickPositions = useMemo(() => {
    if (!chartRef.current) return [];
    const scale = chartRef.current.scales["y"];
    if (!scale) return [];

    return ticks.map((val) => ({
      value: val,
      y: scale.getPixelForValue(val),
    }));
  }, [ticks]);
  const barApproved: ChartDataset<"bar", number[]> = {
    type: "bar",
    label: "Approved",
    data: processedGraphData?.length > 0 ? processedGraphData.map((d: any) => d?.approved) : [],
    backgroundColor: "rgba(234, 95, 95, 1)",
    barThickness: barThickness,
    categoryPercentage: 0.9,
    barPercentage: 0.8,
    borderRadius: 8,
    order: 1,
  };

  const barDone: ChartDataset<"bar", number[]> = {
    type: "bar",
    label: "Done",
    data: processedGraphData?.length > 0 ? processedGraphData.map((d: any) => d?.done) : [],
    backgroundColor: "rgba(255, 224, 206, 1)",
    barThickness: barThickness,
    categoryPercentage: 0.9,
    barPercentage: 0.8,
    borderRadius: 8,
    order: 1,
  };

  const lineCumulative: ChartDataset<"line", number[]> = {
    type: "line",
    label: "Cumulative Approved",
    data: (Array.isArray(processedGraphData) && processedGraphData?.map((d) => d?.cumulativeApproved)) || [],
    borderColor: "rgba(180, 207, 114, 1)",
    borderWidth: 3,
    tension: 0.35,
    pointRadius: 0,
    pointHoverRadius: 4,
    order: 0,
    yAxisID: "y",
    fill: false
  };

  const chartData: ChartData<"bar" | "line"> = {
    labels: Array.isArray(processedGraphData) && processedGraphData.map((d) => d.month) || [],
    datasets: [lineCumulative, barApproved, barDone,],
  };

  const timeOptions = useMemo(() => [
    "Historic",
    "YTD",
    "Custom Selection",
  ], []);

  const filteredYears = useMemo(() => {
    if (!search) return years;
    const term = search.toString();
    return years.filter((year: any) =>
      year.toString().includes(term)
    );
  }, [years, search]);


  const checkMonthSelected = (month: string) => {
    return Array.isArray(value.progressionMonths) ? value.progressionMonths.includes(month) : value.progressionMonths !== month
  }
  const checkYearSelected = useCallback(
    (year: number): boolean => {
      return value.progressionYears?.includes(year) ?? false;
    },
    [value.progressionYears]
  );

  const handleSearch = (event: any) => {
    setSearch(event.target.value)
  }

  const filteredMonths = useMemo(() => {
    return months.filter((item: any) =>
      item
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [months, search,]);

  const checkAllMonthSelect = () => {
    return search !== "" && filteredMonths.length === 0 ? false : value?.progressionMonths.length > 0 && filteredMonths.every((month: string) => value?.progressionMonths.includes(month))
  };

  const checkAllYearSelect = () => {
    return search !== "" && filteredYears.length === 0 ? false : value?.progressionYears.length > 0 && filteredYears.every((year: number) => value?.progressionYears.includes(year))
  };

  const checkSearchExist = () => {
    return search !== ""
  }

  if (loading) {
    return <ProjectProgressChartSkeleton />
  }

  return (
    <Box

      sx={{
        width: "100%",
        bg: colorMode === "light" ? "transparent" : "#252b36",
        p: 3,
        borderRadius: "20px",
        boxShadow: "0 0 40px rgba(209,173,138,0.12)",
        position: 'relative',
      }}
    >
      {/* ---------- LEGEND ---------- */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          position: "sticky",
          top: 0,
          bg: bgDashboard,
          p: 2,
          mb: 1,
          zIndex: 50,
          borderRadius: "10px",
        }}
      >
        {/* Time */}
        <Box sx={{ display: "flex", gap: "10px", alignItems: "center", position: "relative" }} ref={popoverRef}>
          <Box sx={{ display: 'flex', gap: "10px" }}>
            <FilterButton onClick={() => !loading && togglePopover("progressionTime")} label={value.progressionTime} sx={{ minWidth: '113px' }} />
            {isPopoverOpen === "progressionTime" && (
              <Box sx={{ position: 'absolute', gap: '8px', left: 0.1, zIndex: 1, backgroundColor: bgDashboard, boxShadow: '0px 0px 16px rgba(0,0,0,0.06)', borderRadius: '4px', fontSize: '15px', maxWidth: "max-content" }}>
                <OptionList options={timeOptions} selected={value.progressionTime || null} onSelect={(opt) => handleProjectProgressionFilterClick(opt, "progressionTime", "graph-data")} />
              </Box>
            )}
            {value.progressionTime === "Custom Selection" && (<Box sx={{ display: 'flex', gap: '10px' }}>
              <Box sx={{ position: 'relative' }}>
                <FilterButton onClick={() => !loading && togglePopover("Custom Selection")} label={"Select Month"} />
                {isPopoverOpen === "Custom Selection" && (
                  <Box sx={{ right: 0, position: 'absolute' }}>
                    <CustomMonthSelect value={value} search={search} handleSearch={handleSearch} monthsList={filteredMonths} checkMonthSelected={checkMonthSelected} handleMonthSelect={toggleYearMonthHandle} handleAllMonthSelect={handleAllMonthSelection} monthKey={"progressionMonths"} checkAllMonthSelect={checkAllMonthSelect} colorMode={colorMode} flagValue={"graph-data"} checkSearchExist={checkSearchExist} loading={loading} />
                  </Box>
                )}
              </Box>
              <Box sx={{ position: 'relative' }}>
                <FilterButton onClick={() => !loading && togglePopover("years")} label={"Select Year"} />
                {isPopoverOpen === "years" && (
                  <Box sx={{ right: 0, position: 'absolute' }}>
                    <CustomMonthSelect value={value} search={search} handleSearch={handleSearch} monthsList={filteredYears} checkMonthSelected={checkYearSelected} handleMonthSelect={toggleYearMonthHandle} handleAllMonthSelect={handleAllYearSelection} monthKey={"progressionYears"} checkAllMonthSelect={checkAllYearSelect} colorMode={colorMode} flagValue={"graph-data"} checkSearchExist={checkSearchExist} loading={loading} />
                  </Box>
                )}
              </Box>
            </Box>
            )}
          </Box>
        </Box>
        {[
          ["Approved", "rgba(197, 136, 136, 1)"],
          ["Done", "rgba(255,224,206,1)"],
          ["Cumulative Approved", "rgba(180,207,114,1)"],].map(([label, color]) => (
            <Box
              key={label}
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                bg: "rgba(247, 239, 229, 1)",
                px: 3,
                py: 1,
                borderRadius: 8,
                fontSize: 1,
                fontWeight: 500,
                color: "#3c2f25",
              }}
            >
              <Box
                sx={{ width: "12px", height: "12px", borderRadius: "50%", bg: color }}
              />
              {label}
            </Box>
          ))}
      </Box>

      <Box sx={{ display: "flex", width: "100%", position: "relative", pt: 7 }}>
        {/* -------------------------------- LEFT AXIS (Horizontal Title + Ticks) -------------------------------- */}
        <Box
          sx={{
            width: 80,
            position: "sticky",
            left: 0,
            zIndex: 20,
            bg: "transparent",
            height: CHART_HEIGHT,
          }}
        >
          {/* Horizontal Title */}
          <Box
            sx={{
              fontSize: 1,
              fontWeight: 600,
              color: graphlabelColor,
              textAlign: "center",
              mt: -7,
            }}
          >
            Approved
          </Box>

          {/* Numeric Ticks */}
          {tickPositions.map((t) => (
            <Box
              key={t.value}
              sx={{
                position: "absolute",
                left: 7,
                fontSize: "12px",
                color: graphlabelColor,
                top: Math.max(t.y - 10, 0),
              }}
            >
              {t.value}
            </Box>
          ))}
        </Box>

        {/* -------------------------------- SCROLLABLE CHART -------------------------------- */}
        <Box sx={{ width: "100%", overflowX: "auto", overflowY: "hidden", pb: 3, bg: "transparent" }}>
          <Box
            sx={{
              minWidth: '100%',
              width: processedGraphData?.length > 0 && Math.max(processedGraphData?.length * graphWidth, 1200),
              height: CHART_HEIGHT,
            }}
          >
            <Chart<"bar" | "line">
              ref={chartRef}
              type="line" // keep what you already fixed
              data={chartData}
              options={options}
            />
          </Box>
        </Box>

        {/* -------------------------------- RIGHT AXIS (Horizontal Title + Ticks) -------------------------------- */}
        <Box
          sx={{
            width: 80,
            position: "sticky",
            right: 0,
            zIndex: 20,
            bg: "transparent",
            borderLeft: "1px solid #eee",
            height: CHART_HEIGHT,
          }}
        >
          {/* Horizontal Title */}
          <Box
            sx={{
              fontSize: 1,
              fontWeight: 600,
              color: graphlabelColor,
              mt: -7,
            }}
          >
            Done
          </Box>

          {/* Numeric Ticks */}
          {tickPositions.map((t) => (
            <Box
              key={t.value}
              sx={{
                position: "absolute",
                right: 7,
                fontSize: "12px",
                color: graphlabelColor,
                top: Math.max(t.y - 10, 0),
              }}
            >
              {t.value}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

  );
};

export default React.memo(ProjectProgressionChart);
